import PropTypes from "prop-types";
import { Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

const SIZES = {
  small: { variant: "caption", triangle: 8 },
  medium: { variant: "body2", triangle: 9 },
};

/**
 * The one way the metrics screens say "went up" or "went down": a filled
 * triangle plus the absolute amount, green when it rose and red when it fell.
 *
 * `value` is signed so that positive always means "better" — callers whose raw
 * figure runs the other way (a ranking position) flip it before passing it in.
 * Zero renders grey with no triangle, and a missing value renders nothing, or
 * an em dash when `showEmpty` is set, so an unknown is never read as "no change".
 */
export const TrendDelta = ({
  value,
  suffix = "",
  maximumFractionDigits = 2,
  minimumFractionDigits = 0,
  size = "medium",
  showEmpty = false,
  sx,
}) => {
  const theme = useTheme();
  const { variant, triangle } = SIZES[size] || SIZES.medium;

  if (value === null || value === undefined || Number.isNaN(value)) {
    return showEmpty ? (
      <Typography variant={variant} color="text.disabled" sx={sx}>
        —
      </Typography>
    ) : null;
  }

  const isUp = value > 0;
  const isDown = value < 0;
  const color = isUp
    ? theme.palette.success.main
    : isDown
      ? theme.palette.error.main
      : theme.palette.text.secondary;

  const amount = new Intl.NumberFormat("es-ES", {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(Math.abs(value));

  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      component="span"
      sx={{ color, whiteSpace: "nowrap", width: "fit-content", ...sx }}
    >
      {(isUp || isDown) && (
        <svg
          width={triangle}
          height={triangle}
          viewBox="0 0 10 10"
          aria-hidden="true"
          style={{ flexShrink: 0, transform: isDown ? "rotate(180deg)" : undefined }}
        >
          <polygon points="5,1 9.5,9 0.5,9" fill="currentColor" />
        </svg>
      )}
      <Typography
        variant={variant}
        component="span"
        sx={{ fontWeight: 700, color: "inherit", lineHeight: 1.2 }}
      >
        {`${amount}${suffix}`}
      </Typography>
    </Stack>
  );
};

TrendDelta.propTypes = {
  value: PropTypes.number,
  suffix: PropTypes.string,
  maximumFractionDigits: PropTypes.number,
  minimumFractionDigits: PropTypes.number,
  size: PropTypes.oneOf(["small", "medium"]),
  showEmpty: PropTypes.bool,
  sx: PropTypes.object,
};

export default TrendDelta;
